/**
 * The offline write queue's storage layer (`docs/OFFLINE.md` §2).
 *
 * Every unsafe request the app cannot send immediately (`visit`,
 * `registration-case`, `engagements`) is appended here and replayed in order
 * once the network is back (`src/offline/sync.ts`). The storage itself is
 * abstracted behind {@link QueueStorage} so the same module works two ways:
 *
 * - at runtime, an IndexedDB database `dring-offline` with two object
 *   stores, `queue` and `meta`, through a tiny hand-rolled promise wrapper —
 *   no library, per `docs/OFFLINE.md` §2;
 * - in tests, an in-memory implementation: `jsdom` (this project's test
 *   environment) has no IndexedDB, and `fake-indexeddb` is not a dependency
 *   of this package, so tests inject {@link createMemoryQueueStorage}
 *   instead of talking to a browser database.
 *
 * Nothing here performs a network request: that is `src/offline/sync.ts`'s
 * job. This module only ever reads and writes the local store.
 */

/** A queued write's lifecycle, exactly as `docs/OFFLINE.md` §2 names it. */
export type QueueEntryStatus = 'pending' | 'sending' | 'rejected';

/** The error a rejected entry's last attempt failed with. */
export interface QueueEntryError {
  code?: string;
  detail: string;
}

/**
 * One queued write, per `docs/OFFLINE.md` §2's `queue` object store:
 * `{id, createdAt, method, path, body, clientId, attempts, lastError,
 * status}`.
 */
export interface QueueEntry {
  /** Primary key of the `queue` object store. */
  id: string;
  /** ISO timestamp set when the entry was queued; also the replay order. */
  createdAt: string;
  method: 'POST' | 'PATCH';
  /** Path relative to `/api/v1`, e.g. `assignments/12/visit/`. */
  path: string;
  /** The JSON request body, `client_id` included. */
  body: Record<string, unknown>;
  /** The idempotency key minted for this write (`docs/OFFLINE.md` §3). */
  clientId: string;
  attempts: number;
  lastError?: QueueEntryError;
  status: QueueEntryStatus;
}

/** Fields `enqueue()` needs from a caller; the rest is filled in here. */
export type QueueEntryInput = Pick<QueueEntry, 'method' | 'path' | 'body' | 'clientId'>;

/**
 * Storage backend for the queue and its `meta` companion (`lastSyncAt`).
 * Implemented once for IndexedDB (runtime) and once in memory (tests).
 */
export interface QueueStorage {
  add(entry: QueueEntry): Promise<void>;
  list(): Promise<QueueEntry[]>;
  /** Merge `patch` into the stored entry; a no-op if `id` is unknown. */
  update(id: string, patch: Partial<Omit<QueueEntry, 'id'>>): Promise<void>;
  remove(id: string): Promise<void>;
  clear(): Promise<void>;
  getMeta(key: string): Promise<string | undefined>;
  setMeta(key: string, value: string): Promise<void>;
}

// ---------------------------------------------------------------------------
// In-memory implementation (tests).
// ---------------------------------------------------------------------------

/**
 * Build a {@link QueueStorage} backed by a plain `Map`, scoped to whatever
 * holds the returned object — never shared unless the caller shares it.
 *
 * @returns A fresh, empty in-memory queue storage.
 */
export function createMemoryQueueStorage(): QueueStorage {
  const queue = new Map<string, QueueEntry>();
  const meta = new Map<string, string>();

  return {
    async add(entry) {
      queue.set(entry.id, entry);
    },
    async list() {
      return [...queue.values()];
    },
    async update(id, patch) {
      const existing = queue.get(id);
      if (!existing) return;
      queue.set(id, { ...existing, ...patch });
    },
    async remove(id) {
      queue.delete(id);
    },
    async clear() {
      queue.clear();
      meta.clear();
    },
    async getMeta(key) {
      return meta.get(key);
    },
    async setMeta(key, value) {
      meta.set(key, value);
    },
  };
}

// ---------------------------------------------------------------------------
// IndexedDB implementation (runtime).
// ---------------------------------------------------------------------------

const DB_NAME = 'dring-offline';
const DB_VERSION = 1;
const QUEUE_STORE = 'queue';
const META_STORE = 'meta';

/** Open (or create) the `dring-offline` database, its two stores included. */
function openDatabase(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);
    request.onupgradeneeded = () => {
      const db = request.result;
      if (!db.objectStoreNames.contains(QUEUE_STORE)) {
        db.createObjectStore(QUEUE_STORE, { keyPath: 'id' });
      }
      if (!db.objectStoreNames.contains(META_STORE)) {
        db.createObjectStore(META_STORE, { keyPath: 'key' });
      }
    };
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error ?? new Error('Failed to open dring-offline'));
  });
}

/** Run one request against a store inside its own transaction, promisified. */
function runRequest<T>(
  db: IDBDatabase,
  storeName: string,
  mode: IDBTransactionMode,
  build: (store: IDBObjectStore) => IDBRequest<T>,
): Promise<T> {
  return new Promise((resolve, reject) => {
    const transaction = db.transaction(storeName, mode);
    const request = build(transaction.objectStore(storeName));
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error ?? new Error('IndexedDB request failed'));
  });
}

/** One row of the `meta` object store: `{key, value}`. */
interface MetaRow {
  key: string;
  value: string;
}

/**
 * Build a {@link QueueStorage} backed by the browser's IndexedDB, per
 * `docs/OFFLINE.md` §2. The connection opens lazily, on first use, and is
 * reused afterwards.
 *
 * @returns The IndexedDB-backed queue storage.
 */
export function createIndexedDbQueueStorage(): QueueStorage {
  let connection: Promise<IDBDatabase> | undefined;
  const database = (): Promise<IDBDatabase> => {
    connection ??= openDatabase();
    return connection;
  };

  return {
    async add(entry) {
      const db = await database();
      await runRequest(db, QUEUE_STORE, 'readwrite', (store) => store.add(entry));
    },
    async list() {
      const db = await database();
      return runRequest<QueueEntry[]>(db, QUEUE_STORE, 'readonly', (store) => store.getAll());
    },
    async update(id, patch) {
      const db = await database();
      const existing = await runRequest<QueueEntry | undefined>(
        db,
        QUEUE_STORE,
        'readonly',
        (store) => store.get(id),
      );
      if (!existing) return;
      await runRequest(db, QUEUE_STORE, 'readwrite', (store) =>
        store.put({ ...existing, ...patch }),
      );
    },
    async remove(id) {
      const db = await database();
      await runRequest(db, QUEUE_STORE, 'readwrite', (store) => store.delete(id));
    },
    async clear() {
      const db = await database();
      await runRequest(db, QUEUE_STORE, 'readwrite', (store) => store.clear());
      await runRequest(db, META_STORE, 'readwrite', (store) => store.clear());
    },
    async getMeta(key) {
      const db = await database();
      const row = await runRequest<MetaRow | undefined>(db, META_STORE, 'readonly', (store) =>
        store.get(key),
      );
      return row?.value;
    },
    async setMeta(key, value) {
      const db = await database();
      await runRequest(db, META_STORE, 'readwrite', (store) => store.put({ key, value }));
    },
  };
}

// ---------------------------------------------------------------------------
// Active storage + the public queue API.
// ---------------------------------------------------------------------------

/**
 * Broadcasts a `"change"` event whenever the queue is mutated (enqueue,
 * status update, removal, clear): {@link src/offline/useOfflineStatus} and
 * the offline banner listen to it to stay in sync without polling.
 */
export const queueEvents = new EventTarget();

function notifyChange(): void {
  queueEvents.dispatchEvent(new Event('change'));
}

/** `true` once a real IndexedDB is available — false under `jsdom`. */
function hasIndexedDb(): boolean {
  return typeof indexedDB !== 'undefined';
}

let activeStorage: QueueStorage = hasIndexedDb()
  ? createIndexedDbQueueStorage()
  : createMemoryQueueStorage();

/**
 * Swap the storage backend the module-level queue functions use. Tests call
 * this with {@link createMemoryQueueStorage} in `beforeEach`/`afterEach` to
 * get a clean, isolated queue; production code never needs to call it.
 *
 * @param storage - The storage implementation to use from now on.
 */
export function setQueueStorage(storage: QueueStorage): void {
  activeStorage = storage;
}

/** @returns The storage backend currently in use. */
export function getQueueStorage(): QueueStorage {
  return activeStorage;
}

/**
 * Append a write to the queue.
 *
 * @param input - Method, path, body and idempotency key of the write.
 * @returns The persisted entry, `pending`, zero attempts.
 */
export async function enqueue(input: QueueEntryInput): Promise<QueueEntry> {
  const entry: QueueEntry = {
    id: crypto.randomUUID(),
    createdAt: new Date().toISOString(),
    method: input.method,
    path: input.path,
    body: input.body,
    clientId: input.clientId,
    attempts: 0,
    status: 'pending',
  };
  await activeStorage.add(entry);
  notifyChange();
  return entry;
}

/** @returns Every queued entry, in no particular order (callers sort as needed). */
export async function list(): Promise<QueueEntry[]> {
  return activeStorage.list();
}

/**
 * Merge `patch` into a stored entry (e.g. a new `status` or `attempts`
 * count). A no-op if `id` is unknown.
 *
 * @param id - The entry's id.
 * @param patch - Fields to overwrite.
 */
export async function update(id: string, patch: Partial<Omit<QueueEntry, 'id'>>): Promise<void> {
  await activeStorage.update(id, patch);
  notifyChange();
}

/**
 * Remove an entry — a successful replay, or the volunteer dismissing a
 * rejected one ("Retirer").
 *
 * @param id - The entry's id.
 */
export async function remove(id: string): Promise<void> {
  await activeStorage.remove(id);
  notifyChange();
}

/** Empty the queue and its `meta` store — called on logout (`docs/OFFLINE.md` §1). */
export async function clearAll(): Promise<void> {
  await activeStorage.clear();
  notifyChange();
}

/** @returns The ISO timestamp of the last successful sync, or `null`. */
export async function getLastSyncAt(): Promise<string | null> {
  const value = await activeStorage.getMeta('lastSyncAt');
  return value ?? null;
}

/**
 * Record the moment a sync pass last sent something successfully.
 *
 * @param value - ISO timestamp.
 */
export async function setLastSyncAt(value: string): Promise<void> {
  await activeStorage.setMeta('lastSyncAt', value);
  notifyChange();
}
